import { matchRoutes } from "react-router-dom";
import { useCallback, useEffect, useMemo } from "react";
import type { Router, RouterState } from "@remix-run/router";

import { useLocation } from "react-router";
import { arrInsertAt, arrReplaceAt } from "@exonn/utils";
import type {
  RouterTabPath,
  TabDefinition,
  ValidUiModel,
} from "src/lib/tabs/types.ts";
import { convertPathsToIdMap } from "src/lib/tabs/convertPathToIdMap.ts";
import { convertPathToUiModel } from "src/lib/tabs/convertPathToUiModel.ts";
import { normalizePathname } from "src/lib/tabs/normalizePathname.ts";

export const useRouterTabsV2 = <UiModel extends ValidUiModel = ValidUiModel>({
  paths,
  onPathsChange,
  config,
  router,
  fallbackPath,
  onActiveTabChange,
}: {
  router: Router;
  paths: string[];
  fallbackPath?: string;
  config: TabDefinition<UiModel>[];
  onPathsChange: { (paths: string[] | { (paths: string[]): string[] }): void };
  onActiveTabChange?: (tab: { path: string }) => void;
}) => {
  const location = useLocation();

  const syncTabs = useCallback(
    (state: RouterState) => {
      const { matches, location, navigation } = state;

      if (navigation.location) {
        return;
      }

      for (let i = matches.length - 1; i > -1; i--) {
        const match = matches[i];

        for (let i = 0; i < config.length; i++) {
          const def = config[i];
          if (!def.shouldOpen(match)) {
            continue;
          }

          onPathsChange?.((prevPaths: RouterTabPath[]) => {
            const existingPath = prevPaths.find((path: string) => {
              const matches = matchRoutes(router.routes, path) || [];

              for (let i = matches.length - 1; i > -1; i--) {
                const match_ = matches[i];

                if (
                  normalizePathname(match_.pathname) ===
                  normalizePathname(match.pathname)
                ) {
                  return true;
                }
              }
              return false;
            });

            if (existingPath) {
              // update the tab path
              const index = prevPaths.indexOf(existingPath);
              const { pathname, search, hash } = location;

              const path = normalizePathname(pathname) + search + hash;
              onActiveTabChange?.({
                path,
              });
              return arrReplaceAt(prevPaths, index, path);
            } else {
              const { pathname, search, hash } = location;

              const path = normalizePathname(pathname) + search + hash;
              onActiveTabChange?.({
                path,
              });
              return arrInsertAt(prevPaths, def.insertAt(prevPaths), path);
            }
          });
          return;
        }
      }
    },
    [router, onPathsChange, config, onActiveTabChange],
  );

  useEffect(() => {
    // fire immediately
    syncTabs(router.state);
    return router.subscribe(syncTabs);
  }, [syncTabs, router]);

  const tabs = useMemo(() => {
    return paths
      .map((path) =>
        convertPathToUiModel({
          path,
          config,
          router,
        }),
      )
      .filter((tab): tab is UiModel => !!tab);
  }, [paths, config, router]);
  const { pathname, search, hash } = location;

  const currentPath = normalizePathname(pathname) + search + hash;

  const currentModel = useMemo(() => {
    return convertPathToUiModel({
      path: currentPath,
      config,
      router,
    });
  }, [currentPath, config, router]);

  const activeTabId = currentModel?.id;

  const idToPathMap = useMemo(() => {
    return convertPathsToIdMap({
      paths,
      config,
      router,
    });
  }, [paths, router, config]);

  const handleTabsChange = useCallback(
    (tabs: UiModel[]) => {
      onPathsChange?.(tabs.map((tab) => idToPathMap[tab.id]));
    },
    [onPathsChange, idToPathMap],
  );

  const handleActiveTabIdChange = useCallback(
    (id: string | undefined) => {
      if (id) {
        router.navigate(idToPathMap[id]);
        onActiveTabChange?.({
          path: idToPathMap[id],
        });
      } else if (fallbackPath) {
        onActiveTabChange?.({
          path: fallbackPath,
        });
        router.navigate(fallbackPath);
      }
    },
    [router, fallbackPath, idToPathMap, onActiveTabChange],
  );
  return {
    tabs,
    activeTabId,
    activePath: currentPath,
    onActiveTabIdChange: handleActiveTabIdChange,
    onTabsChange: handleTabsChange,
  };
};
